import type { CatalogAgentView, CatalogSectionView } from '../../../apps/apis/commandCenter/types';
import type { AgentBadge, AgentCard, AgentTag, PlannedAgent, SectionSeed } from '../types';
import type { CodeMap } from './agentCode';
import { clampProgress, plannedToCard } from './plannedCard';

/** Per-agent run counters, folded out of the heatmap: the catalog itself carries no run data. */
export interface AgentRunStats {
  runs: number;
  failures: number;
}

export type RunStatsMap = Record<string, AgentRunStats>;

/** A card counts a week of runs, so a weekly agent reads as alive rather than "no runs". */
export const CARD_RUNS_LABEL = 'runs / 7d';

/** Marks a card the board built from runs alone; run health must not replace it. */
export const UNREGISTERED_BADGE: AgentBadge = { text: '미등록', className: 'b-idle' };

/** Shown instead of an empty cell when the server has no owner for a row. */
const NO_OWNER = '—';

/** Runtimes whose stored key is not what the board should print. */
const RUNTIME_LABELS: Record<string, string> = { claude: 'claude code', tg: 'telegram' };

/** The tag under a card; an unrecognised runtime is shown verbatim rather than dropped. */
export const runtimeTag = (runtime: string): AgentTag => ({
  label: RUNTIME_LABELS[runtime] ?? runtime,
  kind: runtime,
});

export const sectionsFromCatalog = (sections: CatalogSectionView[]): SectionSeed[] =>
  [...sections]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((section) => ({
      slug: section.id,
      title: section.title,
      desc: section.description ?? '',
      owner: section.owner ?? NO_OWNER,
      prefix: section.codePrefix,
    }));

export const codesFromCatalog = (agents: CatalogAgentView[]): CodeMap =>
  agents.reduce<CodeMap>((acc, agent) => {
    acc[agent.id] = agent.code;
    return acc;
  }, {});

/** Server card placement: section slug → agent ids in sortOrder. */
export const layoutFromCatalog = (agents: CatalogAgentView[]): Record<string, string[]> =>
  [...agents]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .reduce<Record<string, string[]>>((acc, agent) => {
      acc[agent.sectionId] = [...(acc[agent.sectionId] ?? []), agent.id];
      return acc;
    }, {});

/** A planned catalog row rendered through the same idea model the local capture flow produces. */
const plannedFromCatalog = (agent: CatalogAgentView): PlannedAgent => ({
  id: agent.id,
  section: agent.sectionId,
  name: agent.name,
  objective: agent.objective ?? '',
  owner: agent.owner ?? NO_OWNER,
  note: agent.note ?? '',
  targetDate: agent.targetDate ?? '',
  progress: clampProgress(agent.progress ?? 0),
  createdAt: 0,
});

/**
 * One catalog row as a board card. Everything the old seed carried but the API does not — status dot,
 * badge, run counters — starts idle here and is filled in by `withRunStats` from the heatmap, so a card
 * with no runs reads as "nothing happened", never as a success.
 */
export const cardFromCatalogAgent = (agent: CatalogAgentView): AgentCard => {
  if (agent.lifecycle === 'planned') {
    return plannedToCard(plannedFromCatalog(agent));
  }
  const goal = agent.objective ?? '';
  return {
    id: agent.id,
    section: agent.sectionId,
    name: agent.name,
    goal,
    goalParts: goal ? [{ text: goal, bold: false }] : [],
    sub: agent.description ?? '',
    dot: '',
    badge: { text: 'idle', className: 'b-idle' },
    sched: '',
    tags: [runtimeTag(agent.runtime)],
    owner: agent.owner ?? NO_OWNER,
    runs: 0,
    runsText: '–',
    runsLabel: CARD_RUNS_LABEL,
    succText: 'no runs 7d',
    succTone: '',
    planned: false,
    plan: null,
    loop: null,
  };
};

export const cardsFromCatalog = (agents: CatalogAgentView[]): AgentCard[] => agents.map(cardFromCatalogAgent);

/** One decimal, matching how the board has always printed success. */
const successPercent = (runs: number, failures: number): number => Math.round(((runs - failures) / runs) * 1000) / 10;

/**
 * Folds the heatmap's counters into a card's status. A card the heatmap says nothing about is left as
 * built — idle — because "no runs in the window" and "the heatmap did not load" are the same to the
 * card, and neither justifies claiming success.
 */
export const withRunStats = (card: AgentCard, stats: AgentRunStats | undefined): AgentCard => {
  if (card.planned || !stats || stats.runs === 0) {
    return card;
  }
  const percent = successPercent(stats.runs, stats.failures);
  const healthy = stats.failures === 0;
  const critical = percent < 50;
  return {
    ...card,
    dot: healthy ? 'ok' : critical ? 'crit' : 'warn',
    badge:
      card.badge?.text === UNREGISTERED_BADGE.text
        ? card.badge
        : healthy
          ? null
          : { text: 'missed', className: critical ? 'b-crit' : 'b-warn' },
    runs: stats.runs,
    runsText: String(stats.runs),
    runsLabel: CARD_RUNS_LABEL,
    succText: `success ${percent}%`,
    succTone: healthy ? 'ok' : 'bad',
  };
};

/**
 * Server placement reconciled with the user's drag order.
 * Stored order wins, and a card the user dragged into another section stays there rather than being
 * pulled back by the server's own placement. Ids the server has added since the layout was written are
 * appended rather than lost; ids it no longer serves are dropped, except locally captured ideas, which
 * are passed in explicitly because the server has never heard of them.
 */
export const mergeLayout = (
  server: Record<string, string[]>,
  stored: Record<string, string[]>,
  localIds: Iterable<string> = [],
): Record<string, string[]> => {
  const local = new Set(localIds);
  const served = new Set(Object.values(server).flat());
  const placed = new Set(Object.values(stored).flat());
  const slugs = [...new Set([...Object.keys(server), ...Object.keys(stored)])];
  return slugs.reduce<Record<string, string[]>>((acc, slug) => {
    const kept = (stored[slug] ?? []).filter((id) => served.has(id) || local.has(id));
    acc[slug] = [...kept, ...(server[slug] ?? []).filter((id) => !placed.has(id))];
    return acc;
  }, {});
};
